/**
 * Raffle Cart Validation
 * 
 * This script checks if raffle tickets are in the cart
 * and if there's an active raffle. If no active raffle exists,
 * it either prevents checkout or removes raffle items from cart.
 */

// Raffle product ID (not variant ID)
const RAFFLE_PRODUCT_ID = 14932702626178; // Your raffle product ID

// API endpoint to check if there's an active raffle
const RAFFLE_CHECK_ENDPOINT = 'https://animalfinder.co.uk/Raffle/raffle-status-api.php';

/**
 * Check if cart contains raffle tickets by product ID
 */
function cartContainsRaffleTickets(cart) {
  if (!cart || !cart.items) return false;
  
  return cart.items.some(item => {
    console.log("Checking item:", item);
    return item.product_id == RAFFLE_PRODUCT_ID;
  });
}

/**
 * Remove raffle tickets from cart
 */
async function removeRaffleTicketsFromCart() {
  try {
    // Get current cart
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    // Find all raffle ticket items
    const raffleItems = cart.items.filter(item => 
      item.product_id == RAFFLE_PRODUCT_ID
    );
    
    // If no raffle items, nothing to do
    if (raffleItems.length === 0) return;
    
    // Prepare updates - set quantities to 0
    const updates = {};
    raffleItems.forEach(item => {
      updates[item.id] = 0;
    });
    
    console.log("Removing raffle items:", updates);
    
    // Update cart to remove raffle items
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates })
    });
    
    // Show notification to user
    showNotification(
      'Raffle tickets have been removed from your cart',
      'There is currently no active raffle. Raffle tickets can only be purchased when a raffle is active.',
      'error'
    );
    
    // Refresh the page to update cart display
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  } catch (error) {
    console.error('Error removing raffle tickets:', error);
  }
}

/**
 * Check if there's an active raffle via API
 */
async function checkActiveRaffle() {
  try {
    console.log("Checking active raffle at:", RAFFLE_CHECK_ENDPOINT);
    
    const response = await fetch(RAFFLE_CHECK_ENDPOINT, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      mode: 'cors'
    });
    
    console.log("API response status:", response.status);
    
    if (!response.ok) {
      throw new Error(`API returned status ${response.status}`);
    }
    
    const data = await response.json();
    console.log("API response data:", data);
    
    return data.active === true;
  } catch (error) {
    console.error('Error checking active raffle:', error);
    // If API check fails, assume true to avoid blocking checkout
    return true;
  }
}

/**
 * Show notification to user
 */
function showNotification(title, message, type = 'info') {
  console.log(`Notification: ${title} - ${message}`);
  
  // Fallback notification - create a custom element
  const notificationDiv = document.createElement('div');
  notificationDiv.classList.add('raffle-notification', `raffle-notification--${type}`);
  notificationDiv.innerHTML = `
    <div class="raffle-notification__content">
      <h3>${title}</h3>
      <p>${message}</p>
    </div>
    <button class="raffle-notification__close">&times;</button>
  `;
  
  // Add styles
  const styles = `
    .raffle-notification {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      max-width: 400px;
      padding: 15px;
      background: white;
      border-radius: 5px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      animation: slideIn 0.3s ease-in-out;
    }
    .raffle-notification--error {
      border-left: 4px solid #dc3545;
    }
    .raffle-notification--info {
      border-left: 4px solid #0d6efd;
    }
    .raffle-notification__content {
      margin-right: 20px;
    }
    .raffle-notification__content h3 {
      margin-top: 0;
      margin-bottom: 5px;
    }
    .raffle-notification__content p {
      margin: 0;
    }
    .raffle-notification__close {
      position: absolute;
      top: 10px;
      right: 10px;
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      padding: 0;
      width: 20px;
      height: 20px;
      line-height: 20px;
      text-align: center;
    }
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  
  const styleElement = document.createElement('style');
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
  
  // Add close button functionality
  const closeButton = notificationDiv.querySelector('.raffle-notification__close');
  closeButton.addEventListener('click', () => {
    notificationDiv.remove();
    styleElement.remove();
  });
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (document.body.contains(notificationDiv)) {
      notificationDiv.remove();
      styleElement.remove();
    }
  }, 10000);
  
  // Add to document
  document.body.appendChild(notificationDiv);
}

/**
 * Go to checkout. Always a plain page change: clicking the button again would
 * land back in the click listener below and never leave the page.
 */
function proceedToCheckout() {
  window.location.href = '/checkout';
}

/**
 * Handle a click on a checkout button
 */
function handleCheckoutClick(event) {
  // Stop the normal submit while the cart is checked
  event.preventDefault();

  fetch('/cart.js')
    .then(response => response.json())
    .then(async cart => {
      if (!cartContainsRaffleTickets(cart)) {
        proceedToCheckout();
        return;
      }

      const activeRaffleExists = await checkActiveRaffle();

      if (activeRaffleExists) {
        proceedToCheckout();
      } else if (cart.items.length > cart.items.filter(item => item.product_id == RAFFLE_PRODUCT_ID).length) {
        // Cart has other items besides raffle tickets: remove just the raffle tickets
        await removeRaffleTicketsFromCart();
      } else {
        // Cart only has raffle tickets: show message and don't proceed
        showNotification(
          'Raffle Tickets Unavailable',
          'There is currently no active raffle. Raffle tickets can only be purchased when a raffle is active.',
          'error'
        );
      }
    })
    .catch(error => {
      console.error('Error checking cart:', error);
      // On error, let the checkout proceed normally
      proceedToCheckout();
    });
}

/**
 * Initialize cart validation
 */
function initCartValidation() {
  // Catches the cart page and cart drawer checkout buttons, including a tap on
  // anything inside the button.
  document.addEventListener('click', function(e) {
    if (e.target.closest('button[name="checkout"], input[name="checkout"]')) {
      handleCheckoutClick(e);
    }
  });

  // Check cart on page load
  fetch('/cart.js')
    .then(response => response.json())
    .then(async cart => {
      console.log("Initial cart check:", cart);
      
      // If cart has raffle tickets, check if there's an active raffle
      if (cartContainsRaffleTickets(cart)) {
        const activeRaffleExists = await checkActiveRaffle();
        
        if (!activeRaffleExists) {
          // If no active raffle, remove raffle tickets
          console.log("No active raffle on page load, removing raffle tickets");
          await removeRaffleTicketsFromCart();
        }
      }
    })
    .catch(error => {
      console.error('Error checking cart on load:', error);
    });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartValidation);
} else {
  initCartValidation();
}